import React from 'react';
import SEOHead from '../SEOHead';
import { Card, CardContent } from '../ui/card';
import { TwitterTweetEmbed } from 'react-twitter-embed';

const MediaPage = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Exlo Media Features",
    "description": "Read about Exlo in various media outlets, blog posts, and social media features.",
    "url": "https://music.exlo.no/media",
    "mainEntity": {
      "@type": "MusicGroup",
      "name": "Exlo",
      "url": "https://music.exlo.no"
    },
    "hasPart": [
      {
        "@type": "BlogPosting",
        "name": "Exlo on Medium",
        "url": "https://exlomusic.medium.com/",
        "author": {
          "@type": "MusicGroup",
          "name": "Exlo"
        }
      },
      {
        "@type": "BlogPosting",
        "name": "Introducing Exlo - Bandcamp Diaries",
        "url": "https://thebandcampdiaries.com/post/757622959586377728/introducing-exlo"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#1a1a2e] text-white">
      <SEOHead
        title="Exlo Media Features - Press and Social Media Coverage"
        description="Read about Exlo in various media outlets, blog posts, and social media features."
        url="/media"
        structuredData={structuredData}
      />
      
      <noscript>
        <div className="p-8">
          <h1 className="text-3xl font-bold mb-4 text-[#e94560]">Media Features</h1>
          <div className="mb-6">
            <h2 className="text-xl font-bold mb-2 text-[#e94560]">Blog Posts</h2>
            <ul className="list-disc list-inside space-y-2">
              <li><a href="https://exlomusic.medium.com/" className="text-cyan-400 underline">Exlo on Medium</a></li>
              <li><a href="https://thebandcampdiaries.com/post/757622959586377728/introducing-exlo" className="text-cyan-400 underline">Introducing Exlo - Bandcamp Diaries</a></li>
            </ul>
          </div>
          <div className="mb-6">
            <h2 className="text-xl font-bold mb-2 text-[#e94560]">Social Media</h2>
            <ul className="list-disc list-inside space-y-2">
              <li><a href="https://x.com/TeAmExlo" className="text-cyan-400 underline">Follow on Twitter</a></li>
              <li><a href="https://www.instagram.com/exlo_84/" className="text-cyan-400 underline">Follow on Instagram</a></li>
            </ul>
          </div>
        </div>
      </noscript>

      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        <header className="mb-8">
          <h1 className="text-4xl font-bold mb-4 text-[#e94560]">Media Features</h1>
          <p className="text-lg text-gray-300">Read about Exlo in various media outlets, blog posts, and social media features.</p>
        </header>

        <section className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-[#e94560]">Blog Posts</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-transparent border-[#e94560] hover:border-cyan-400 transition-colors">
              <CardContent className="p-4">
                <h3 className="text-xl font-bold mb-2 text-[#e94560]">Medium</h3>
                <p className="text-white text-lg font-light leading-relaxed mb-4">
                  Discover more about Exlo in the detailed blog posts on Medium, featuring insights into the creative process and musical journey.
                </p>
                <a 
                  href="https://exlomusic.medium.com/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-cyan-400 underline hover:text-cyan-300 transition-colors"
                >
                  Read on Medium
                </a>
              </CardContent>
            </Card>

            <Card className="bg-transparent border-[#e94560] hover:border-cyan-400 transition-colors">
              <CardContent className="p-4">
                <h3 className="text-xl font-bold mb-2 text-[#e94560]">Bandcamp Diaries</h3>
                <p className="text-white text-lg font-light leading-relaxed mb-4">
                  An introduction to Exlo featured on Bandcamp Diaries, exploring the unique approach to AI-assisted music creation.
                </p>
                <a 
                  href="https://thebandcampdiaries.com/post/757622959586377728/introducing-exlo" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-cyan-400 underline hover:text-cyan-300 transition-colors"
                >
                  Read on Bandcamp Diaries
                </a>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-[#e94560]">Twitter Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-transparent border border-[#e94560] rounded-lg p-4">
              <TwitterTweetEmbed tweetId="1819055337577550094" />
            </div>
            <div className="bg-transparent border border-[#e94560] rounded-lg p-4">
              <TwitterTweetEmbed tweetId="1807432687654686938" />
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-[#e94560]">Reddit Discussions</h2>
          <Card className="bg-transparent border-[#e94560] hover:border-cyan-400 transition-colors">
            <CardContent className="p-4">
              <h3 className="text-xl font-bold mb-2 text-[#e94560]">r/MusicNews</h3>
              <p className="text-white text-lg font-light leading-relaxed mb-4">
                Join the discussions and comments on Reddit about Exlo's music and creative process.
              </p>
              <a 
                href="https://www.reddit.com/r/MusicNews/comments/1ehls2n/the_bandcamp_diaries_introducing_exlo/?utm_source=share&utm_medium=web3x&utm_name=web3xcss&utm_term=1&utm_content=share_button" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-cyan-400 underline hover:text-cyan-300 transition-colors"
              >
                Read on Reddit
              </a>
            </CardContent>
          </Card>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-[#e94560]">Instagram Features</h2>
          <Card className="bg-transparent border-[#e94560] hover:border-cyan-400 transition-colors">
            <CardContent className="p-4">
              <h3 className="text-xl font-bold mb-2 text-[#e94560]">Instagram Post</h3>
              <p className="text-white text-lg font-light leading-relaxed mb-4">
                Follow Exlo on Instagram for behind-the-scenes content and updates.
              </p>
              <a 
                href="https://www.instagram.com/p/C-Io1qTsk75/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-cyan-400 underline hover:text-cyan-300 transition-colors"
              >
                View on Instagram
              </a>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default MediaPage;
